import React from "react";
import { Button, Form, Input, InputNumber, Modal, Select } from "antd";
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import CustomDatePicker from "@components/CustomDatePicker";
import "./MobileTestItemsCardList.css";

const { TextArea } = Input;
const { Option } = Select;

/** Compact inline form-item layout for card fields */
const cardFormItemLayout = {
    style: { marginBottom: 8 },
    className: "mobile-test-item-edit-field",
    labelCol: { flex: "0 0 90px" },
    wrapperCol: { flex: "1 1 0" },
    labelAlign: "left",
};

/** Render display value for a field (read-only card) */
const renderDisplayValue = (value, col) => {
    const t = (col?.type || "text").trim().toLowerCase() || "text";

    if (value === null || value === undefined || value === "") {
        return "—";
    }

    switch (t) {
        case "boolean":
            if (value === true) return <span className="mobile-test-item-pass"><CheckCircleOutlined /> Pass</span>;
            if (value === false) return <span className="mobile-test-item-fail"><CloseCircleOutlined /> Fail</span>;
            return "—";
        case "photo":
            if (value && typeof value === "object" && (value.kpi_file_id || value.image))
                return <span className="mobile-test-item-photo-indicator">📷 Photo attached</span>;
            return "—";
        case "date":
            return String(value);
        case "label":
            return <span style={{ color: "#8c8c8c" }}>—</span>;
        case "na":
            return <span style={{ color: "#8c8c8c" }}>N/A</span>;
        default:
            return String(value);
    }
};

/**
 * Mobile card-based view for Test Items in FunctionalTesting detail page.
 * Read-only mode: display cards with field values.
 * Edit mode: inline editable cards with Form.Items bound directly to Form.List.
 */
const MobileTestItemsCardList = ({
    fields,
    add,
    remove,
    form,
    readOnly,
    measurementCols,
    templateMeasurementColumns,
    templateFieldCells,
    templateRowsMeta,
    showLegacyFacilityTypeColumn,
    equipmentForFiles,
    resolvedFacilityId,
    id,
    isCreatePage,
    newItemDefaults,
    buildEffectiveMeasurementCell,
    normalizeMeasurementColumnType,
    normalizeMeasurementSelectOptionsFromFields,
    buildFunctionalTestingMeasurementCellRules,
    FunctionalTestingPhotoFormControl,
    PassFailToggleButtons,
}) => {
    // Delete handlers
    const handleDeleteClick = (fieldIndex) => {
        Modal.confirm({
            title: "Delete Test Item",
            content: "Are you sure you want to delete this test item?",
            okText: "Delete",
            cancelText: "Cancel",
            okType: "danger",
            onOk: () => remove(fieldIndex),
        });
    };

    // Add handler: directly add a new editable card
    const handleAddItem = () => {
        add(newItemDefaults());
    };

    // Compute effective measurement cell for a column in a given row
    const getEffectiveCell = (col, rowItem) => {
        return buildEffectiveMeasurementCell(col, rowItem, templateFieldCells || {});
    };

    // Render a single measurement column field: edit or read-only
    const renderMeasurementField = (col, field, rowItem) => {
        const effectiveCol = getEffectiveCell(col, rowItem);
        const effType = normalizeMeasurementColumnType(effectiveCol.type);
        const isLabelCol = effType === "label";
        const isNaCol = effType === "na";

        const rowMeta = templateRowsMeta != null && rowItem?._ftRowId
            ? templateRowsMeta.find((r) => String(r.id) === String(rowItem._ftRowId))
            : null;
        const rowRequired = !!(rowItem?._ftRowRequired || rowMeta?.required);
        const cellLevelReq = !!effectiveCol._ftCellRequired;
        const cellRequired = !readOnly && !isLabelCol && !isNaCol &&
            (Boolean(col.required) || rowRequired || cellLevelReq);
        const cellRules = buildFunctionalTestingMeasurementCellRules(effType, cellRequired);

        const labelNode = (
            <span>
                {col.label ?? col.id}
                {cellRequired && <span style={{ color: "#ff4d4f", marginLeft: 2 }}>*</span>}
            </span>
        );

        if (isLabelCol) {
            return (
                <div key={col.id} className="mobile-test-item-field">
                    <span className="mobile-test-item-label">{labelNode}:</span>
                    <span className="mobile-test-item-value" style={{ color: "#8c8c8c" }}>—</span>
                </div>
            );
        }
        if (isNaCol) {
            return (
                <div key={col.id} className="mobile-test-item-field">
                    <span className="mobile-test-item-label">{labelNode}:</span>
                    <span className="mobile-test-item-value" style={{ color: "#8c8c8c" }}>N/A</span>
                </div>
            );
        }

        if (readOnly) {
            const value = rowItem?.[col.id];
            return (
                <div key={col.id} className="mobile-test-item-field">
                    <span className="mobile-test-item-label">{labelNode}:</span>
                    <span className="mobile-test-item-value">{renderDisplayValue(value, effectiveCol)}</span>
                </div>
            );
        }

        // Edit mode: render inline Form.Item
        if (effType === "photo") {
            return (
                <Form.Item
                    key={col.id}
                    {...cardFormItemLayout}
                    label={labelNode}
                    name={[field.name, col.id]}
                    rules={cellRules}
                >
                    <FunctionalTestingPhotoFormControl
                        readOnly={false}
                        equipmentId={equipmentForFiles}
                        facilityId={resolvedFacilityId}
                        functionalTestingRecordId={isCreatePage ? null : id}
                    />
                </Form.Item>
            );
        }

        if (effType === "boolean") {
            return (
                <Form.Item
                    key={col.id}
                    {...cardFormItemLayout}
                    label={labelNode}
                    name={[field.name, col.id]}
                    rules={cellRules}
                >
                    <PassFailToggleButtons readOnly={false} />
                </Form.Item>
            );
        }

        // text, number, select, textarea, date
        const selectOpts = effType === "select"
            ? normalizeMeasurementSelectOptionsFromFields(effectiveCol)
            : [];
        const fullWidth = { width: "100%" };

        let inputNode;
        switch (effType) {
            case "number":
                inputNode = <InputNumber controls={false} style={fullWidth} />;
                break;
            case "select":
                inputNode = (
                    <Select allowClear style={fullWidth} placeholder="Select">
                        {selectOpts.map((opt) => (
                            <Option key={String(opt)} value={opt}>{String(opt)}</Option>
                        ))}
                    </Select>
                );
                break;
            case "textarea":
                inputNode = <TextArea rows={2} autoSize={{ minRows: 2, maxRows: 6 }} style={fullWidth} />;
                break;
            case "date":
                inputNode = <CustomDatePicker format="YYYY-MM-DD" style={fullWidth} />;
                break;
            default:
                inputNode = <Input style={fullWidth} />;
                break;
        }

        return (
            <Form.Item
                key={col.id}
                {...cardFormItemLayout}
                label={labelNode}
                name={[field.name, col.id]}
                rules={cellRules}
            >
                {inputNode}
            </Form.Item>
        );
    };

    return (
        <div className="mobile-test-items-card-list">
            {fields.length === 0 ? (
                <div className="mobile-test-items-empty">No test items</div>
            ) : (
                fields.map((field) => {
                    const rowData = form.getFieldValue(["value", field.name]) || {};
                    const cardTitle = rowData.name || "Item " + (field.name + 1);

                    if (readOnly) {
                        // Read-only card (display only)
                        return (
                            <div key={field.key} className="mobile-test-item-card">
                                <div className="mobile-test-item-card-header">
                                    <span className="mobile-test-item-card-title">{cardTitle}</span>
                                </div>
                                <div className="mobile-test-item-card-body">
                                    {showLegacyFacilityTypeColumn && rowData.type && (
                                        <div className="mobile-test-item-field">
                                            <span className="mobile-test-item-label">Type:</span>
                                            <span className="mobile-test-item-value">{rowData.type}</span>
                                        </div>
                                    )}
                                    {rowData.description && (
                                        <div className="mobile-test-item-field">
                                            <span className="mobile-test-item-label">Description:</span>
                                            <span className="mobile-test-item-value">{rowData.description}</span>
                                        </div>
                                    )}
                                    {templateMeasurementColumns !== null && rowData.unit && (
                                        <div className="mobile-test-item-field">
                                            <span className="mobile-test-item-label">Unit:</span>
                                            <span className="mobile-test-item-value">{rowData.unit}</span>
                                        </div>
                                    )}
                                    {measurementCols.map((col) => renderMeasurementField(col, field, rowData))}
                                </div>
                            </div>
                        );
                    }

                    // Editable card (inline Form.Items)
                    return (
                        <div key={field.key} className="mobile-test-item-card mobile-test-item-card--editable">
                            <div className="mobile-test-item-card-header">
                                <span className="mobile-test-item-card-title">
                                    Item {field.name + 1}
                                </span>
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteClick(field.name)}
                                />
                            </div>
                            <div className="mobile-test-item-card-body">
                                {showLegacyFacilityTypeColumn && (
                                    <Form.Item
                                        {...cardFormItemLayout}
                                        label="Type"
                                        name={[field.name, "type"]}
                                    >
                                        <Input placeholder="e.g. AHU" style={{ width: "100%" }} />
                                    </Form.Item>
                                )}
                                <Form.Item
                                    {...cardFormItemLayout}
                                    label="Name"
                                    name={[field.name, "name"]}
                                    rules={[{ required: true, message: "This field is required" }]}
                                >
                                    <Input placeholder="Test item name" style={{ width: "100%" }} />
                                </Form.Item>
                                <Form.Item
                                    {...cardFormItemLayout}
                                    label="Description"
                                    name={[field.name, "description"]}
                                >
                                    <Input placeholder="Optional description" style={{ width: "100%" }} />
                                </Form.Item>
                                {templateMeasurementColumns !== null && (
                                    <Form.Item
                                        {...cardFormItemLayout}
                                        label="Unit"
                                        name={[field.name, "unit"]}
                                    >
                                        <Input placeholder="e.g. °F, %" style={{ width: "100%" }} />
                                    </Form.Item>
                                )}
                                {measurementCols.map((col) => renderMeasurementField(col, field, rowData))}
                            </div>
                        </div>
                    );
                })
            )}

            {/* Add Button: directly appends a new editable card */}
            {!readOnly && (
                <Button
                    type="dashed"
                    onClick={handleAddItem}
                    block
                    icon={<PlusOutlined />}
                    className="mobile-test-items-add-button"
                >
                    Add Test Item
                </Button>
            )}

        </div>
    );
};

export default MobileTestItemsCardList;
